'use client'

import { useCurrency } from '@/hooks/use-currency'
import { IProductVariant } from '@/lib/types/core/product-variant'
import { getUnitDisplay } from '@/lib/types/core/unit-of-measure'
import { useState } from 'react'
import { AllColorsModal } from './all-colors-modal'
import { ColorSwatchRail } from './color-swatch-rail'
import { ModeToggle } from './mode-toggle'
import { VariantQuantityInput } from './variant-quantity-input'

interface VariantSelectorProps {
  variants: IProductVariant[]
  variantInventory: Array<{ variantId: string; inventory: number; inStock: boolean }>
  selection: {
    variantId: string | null
    mode: 'cut' | 'roll'
    quantity: number
  }
  selectedVariant: IProductVariant | null
  canShowRollMode: boolean
  derivedPrice: number
  /** Price for one unit in the current mode, from the same resolver as derivedPrice. */
  derivedUnitPrice: number
  availableInventory: number
  minQty: number
  maxQty: number
  stepSize: number
  onSelectColor: (variantId: string) => void
  onSelectMode: (mode: 'cut' | 'roll') => void
  onSelectQuantity: (qty: number) => void
}

export function VariantSelector({
  variants,
  variantInventory,
  selection,
  selectedVariant,
  canShowRollMode,
  derivedPrice,
  derivedUnitPrice,
  availableInventory,
  minQty,
  maxQty,
  stepSize,
  onSelectColor,
  onSelectMode,
  onSelectQuantity
}: VariantSelectorProps) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { format: formatCurrency } = useCurrency()

  const isLowStock = availableInventory > 0 && availableInventory <= 5

  return (
    <div className='space-y-4'>
      {/* Color Swatches */}
      <ColorSwatchRail
        variants={variants}
        selectedVariantId={selection.variantId}
        variantInventory={variantInventory}
        onSelectColor={onSelectColor}
        onShowAll={() => setIsModalOpen(true)}
      />

      {/* All Colors Modal */}
      <AllColorsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        variants={variants}
        selectedVariantId={selection.variantId}
        variantInventory={variantInventory}
        onSelectColor={(id) => {
          onSelectColor(id)
          setIsModalOpen(false)
        }}
      />

      {/* Mode Toggle — only when variant supports rolls */}
      {selectedVariant && canShowRollMode && (
        <ModeToggle
          mode={selection.mode}
          rollPrice={Number(selectedVariant.rollPrice ?? 0)}
          onSelectMode={onSelectMode}
        />
      )}

      {/* Quantity Input — only when a variant is selected and in stock */}
      {selectedVariant && availableInventory > 0 && (
        <VariantQuantityInput
          quantity={selection.quantity}
          min={minQty}
          max={maxQty}
          step={stepSize}
          mode={selection.mode}
          unitDisplay={getUnitDisplay(selectedVariant.unitOfMeasure || 'EACH')}
          onQuantityChange={onSelectQuantity}
        />
      )}

      {/* Price Display */}
      {selectedVariant && (
        <div aria-live='polite' className='space-y-1'>
          <div className='text-lg font-bold'>
            {selection.mode === 'cut' ? (
              <>
                {formatCurrency(derivedUnitPrice)} &times; {selection.quantity}{' '}
                = <span className='text-foreground'>{formatCurrency(derivedPrice)}</span>
              </>
            ) : (
              <>
                {formatCurrency(derivedUnitPrice)} / roll &times; {selection.quantity}{' '}
                = <span className='text-foreground'>{formatCurrency(derivedPrice)}</span>
              </>
            )}
          </div>
          {isLowStock && (
            <p className='text-sm text-warning font-medium'>
              Only {availableInventory} {selection.mode === 'roll' ? 'rolls' : 'left'}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
